import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload, ArrowLeft, Sparkles, Loader2, FileText,
  LinkedinIcon, Link2, ClipboardPaste, AlertCircle, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "linkedin", label: "LinkedIn URL", icon: LinkedinIcon },
  { id: "paste",    label: "Paste Text",   icon: ClipboardPaste },
  { id: "file",     label: "Upload File",  icon: Upload },
];

export default function ResumeImport() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [tab, setTab]         = useState("linkedin");
  const [url, setUrl]         = useState("");
  const [pasteText, setPaste] = useState("");
  const [file, setFile]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [dragging, setDragging] = useState(false);

  const token = () => localStorage.getItem("token");

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ["application/pdf", "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/plain"];
    if (!allowed.includes(f.type) && !f.name.match(/\.(pdf|doc|docx|txt)$/i)) {
      toast.error("Please upload a PDF, DOCX, or TXT file"); return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5 MB"); return; }
    setFile(f);
  };

  const importLinkedInURL = async () => {
    if (!url.trim()) { toast.error("Enter a LinkedIn URL"); return; }
    setLoading(true); setBlocked(false);
    try {
      const res = await fetch("/api/resume/linkedin-url", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ url: url.trim() }),
      });
      if (res.status === 403) { setBlocked(true); setLoading(false); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Import failed");
      }
      const data = await res.json();
      toast.success("Profile imported — review your details below");
      nav("/app/resume/new", { state: { prefill: data } });
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const importText = async () => {
    if (pasteText.trim().length < 50) { toast.error("Paste more profile text"); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/resume/linkedin-text", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ text: pasteText }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Parse failed"); }
      const data = await res.json();
      toast.success("Profile parsed — review your details below");
      nav("/app/resume/new", { state: { prefill: data } });
    } catch (e) {
      toast.error(e.message || "Parse failed");
    } finally {
      setLoading(false);
    }
  };

  const analyzeFile = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch("/api/resume/file", {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Analysis failed");
      }
      const data = await res.json();
      toast.success("Resume analyzed — review your details below");
      nav("/app/resume/new", { state: { prefill: data } });
    } catch (e) {
      toast.error(e.message || "Could not analyze file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-10 py-10 max-w-3xl">
      <button onClick={() => nav("/app/resume")} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-8 transition">
        <ArrowLeft className="w-4 h-4" /> Back to Resume Builder
      </button>

      <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Import Your Profile</h1>
      <p className="text-neutral-500 mb-8">AI extracts your experience, skills, and education — then pre-fills the resume builder for you.</p>

      {/* Tab switcher */}
      <div className="flex border border-white/10 mb-8 p-0.5 gap-0.5">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setTab(id); setBlocked(false); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-semibold uppercase tracking-wider transition ${
              tab === id ? "bg-white text-black" : "text-neutral-400 hover:text-white"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* LinkedIn URL tab */}
      {tab === "linkedin" && (
        <div className="space-y-5">
          <div className="border border-white/10 p-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">LinkedIn Profile URL</label>
              <div className="flex gap-2 mt-2">
                <div className="flex items-center flex-1 border border-white/10 focus-within:border-[#0055FF]">
                  <Link2 className="w-4 h-4 text-neutral-600 ml-3 shrink-0" />
                  <input
                    value={url}
                    onChange={(e) => { setUrl(e.target.value); setBlocked(false); }}
                    placeholder="https://www.linkedin.com/in/username"
                    className="flex-1 bg-transparent px-3 py-2.5 text-sm outline-none"
                  />
                </div>
                <button
                  onClick={importLinkedInURL}
                  disabled={loading || !url.trim()}
                  className="flex items-center gap-2 bg-[#0055FF] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Import
                </button>
              </div>
            </div>

            {/* Blocked state — show text paste fallback inline */}
            {blocked && (
              <div className="border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-300">LinkedIn blocked the request</p>
                    <p className="text-xs text-neutral-400 mt-1">
                      LinkedIn requires login to view full profiles. Copy your profile text and paste it below — works instantly.
                    </p>
                  </div>
                </div>
                <ol className="text-xs text-neutral-400 space-y-1 pl-4 list-decimal">
                  <li>Open your LinkedIn profile in a browser</li>
                  <li>Select all text on the page (Ctrl+A / Cmd+A)</li>
                  <li>Copy and paste it below</li>
                </ol>
                <button
                  onClick={() => setTab("paste")}
                  className="text-xs text-[#D3FF24] border border-[#D3FF24]/30 px-3 py-1.5 hover:bg-[#D3FF24]/10 transition"
                >
                  Switch to paste text →
                </button>
              </div>
            )}
          </div>

          <div className="border border-white/10 p-5 bg-[#0A0A0A]">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">What gets extracted</p>
            <div className="grid grid-cols-2 gap-2">
              {["Name & contact info","Professional summary","Work experience + dates","Key achievements per role","Education & degrees","Skills & technologies"].map((s) => (
                <div key={s} className="flex items-center gap-2 text-xs text-neutral-400">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#D3FF24] shrink-0" strokeWidth={1.75} /> {s}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Paste text tab */}
      {tab === "paste" && (
        <div className="space-y-5">
          <div className="border border-white/10 p-6 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Paste LinkedIn Profile Text</label>
              <p className="text-xs text-neutral-500 mt-1 mb-3">
                Go to your LinkedIn profile → select all (Ctrl+A / Cmd+A) → copy → paste here.
              </p>
              <textarea
                rows={12}
                value={pasteText}
                onChange={(e) => setPaste(e.target.value)}
                placeholder="Paste your LinkedIn profile text here…"
                className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none"
              />
            </div>
            <button
              onClick={importText}
              disabled={loading || pasteText.trim().length < 50}
              className="w-full flex items-center justify-center gap-2 bg-[#0055FF] text-white py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Sparkles className="w-4 h-4" /> Parse & Pre-fill Form</>}
            </button>
          </div>
        </div>
      )}

      {/* File upload tab */}
      {tab === "file" && (
        <div className="space-y-5">
          <div
            onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed transition cursor-pointer p-16 text-center ${
              dragging ? "border-[#D3FF24] bg-[#D3FF24]/5" : "border-white/15 hover:border-[#D3FF24]/40"
            }`}
          >
            {file ? (
              <div>
                <FileText className="w-10 h-10 text-[#D3FF24] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-white font-semibold">{file.name}</p>
                <p className="text-xs text-neutral-500 mt-1">{(file.size / 1024).toFixed(0)} KB · Click to change</p>
              </div>
            ) : (
              <div>
                <Upload className="w-10 h-10 text-neutral-600 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-neutral-300 font-medium">Drop your resume here or click to browse</p>
                <p className="text-xs text-neutral-500 mt-2">PDF, DOCX or TXT · Max 5 MB</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" className="hidden"
              onChange={(e) => handleFile(e.target.files[0])} />
          </div>
          <button
            onClick={analyzeFile}
            disabled={!file || loading}
            className="w-full flex items-center justify-center gap-2 bg-premium text-white py-4 font-semibold uppercase tracking-widest text-xs hover:brightness-110 transition disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Analyze & Enhance with AI
          </button>
        </div>
      )}
    </div>
  );
}
